import { config } from 'lib-shared-backend-config';
import type { MetaConversation as MetaConversationT } from 'lib-shared-legacy-types';
import orderBy from 'lodash/orderBy';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';

import type { RootReducerStateT } from 'app/reducers/rootReducer';
import { bindRtkActionToPromise } from 'app/utils/promiseActions';
import { AppDispatch } from 'bootstrap.store';
import { TypingType } from 'components/conversation/reducers/typing';
import { userModalActions } from 'components/usersModal';
import { websocketActions } from 'middleware/websocket/actions/websocket';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

import * as conversationsActions from '../../conversation/actions/conversations';
import * as typingActions from '../../conversation/actions/typing';
import * as personalActions from '../../settings/actions/personal';
import { SidebarMenu } from '../components/sidebarMenu';
import type {
  DispatchPropsT,
  LeftMenuTogglePayloadT,
  OwnPropsT,
  StatePropsT,
} from '../components/sidebarMenu';

const { conversationsRemove } = conversationsActions.actions;
const { typingRemove } = typingActions.actions;
const { personalLeftMenu } = personalActions.actions;

const menuDevNotices: StatePropsT['menuDevNotices'] = [];

if (config.backendDomain) {
  menuDevNotices.push({
    title: 'BD',
    label: `Backend Domain: ${config.backendDomain}`,
    onClose: () => {
      sessionStorage.removeItem('dixa:backend-domain');
      window.location.reload();
    },
  });
}

const getMetaConversations = (state: RootReducerStateT) =>
  state.conversations.metaConversations;
const sortMetaConversations = (item: MetaConversationT) => {
  if (item.conversation.status === 'open') {
    return 1;
  }
  if (['awaitingpending', 'pending'].includes(item.conversation.status || '')) {
    return 2;
  }
  if (item.conversation.status === 'closed') {
    return 3;
  }

  return 4;
};

const getMetaConversationsSorted = (state: RootReducerStateT) => {
  return orderBy(
    getMetaConversations(state),
    [sortMetaConversations, 'isAssignedToMe', 'conversation.id'],
    ['asc', 'desc', 'desc'],
  );
};

const BREAK_POINT_FOR_HIDING = 5;
const getMetaConversationsBeforeAndAfterBreak = createSelector(
  [
    getMetaConversationsSorted,
    (state: RootReducerStateT) =>
      state.presence.presence.presence_status.load_descriptions.filter(
        (load_description) => load_description.load_type === 'acw',
      ),
  ],
  (metaConversations, loadDescriptions) => {
    const before: MetaConversationT[] = [];
    const after: MetaConversationT[] = [];
    metaConversations.forEach((metaConversation, index) => {
      if (
        metaConversation.conversation.status === 'closed' &&
        loadDescriptions.some(
          (loadDescription) =>
            metaConversation.conversation.id === loadDescription.csid,
        )
      ) {
        before.unshift(metaConversation);
      } else if (
        index < BREAK_POINT_FOR_HIDING ||
        metaConversation.conversation.status === 'open'
      ) {
        before.push(metaConversation);
      } else {
        after.push(metaConversation);
      }
    });

    return { before, after };
  },
);

const mapStateToProps = (state: RootReducerStateT): StatePropsT => {
  const { before, after } = getMetaConversationsBeforeAndAfterBreak(state);

  return {
    search: state.search,
    metaConversation: state.conversation.metaConversation,
    metaConversations: state.conversations.metaConversations,
    metaConversationsBeforeBreak: before,
    metaConversationsAfterBreak: after,
    typings: state.typing.typings,
    conversationsLastSearchParams: state.conversationsPage.lastSearchParams,
    isMenuOpen: state.settings.personal.isLeftMenuOpen,
    showWidget: !!state.widget.presence?.show_widget,
    menuDevNotices,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch): DispatchPropsT => {
  return {
    showUserModal: () =>
      dispatch(userModalActions.show({ user: { type: 'member' } })),
    typingRemove: (typing: TypingType) => dispatch(typingRemove(typing)),
    conversationsRemove: (csid: number) => dispatch(conversationsRemove(csid)),
    wsRequest: bindRtkActionToPromise(dispatch, websocketActions.request),
    personalLeftMenuToggle: bindActionToPromise<
      LeftMenuTogglePayloadT,
      unknown,
      unknown
    >(dispatch, personalLeftMenu),
  };
};

export const Menu = connect<
  StatePropsT,
  DispatchPropsT,
  OwnPropsT,
  RootReducerStateT
>(
  mapStateToProps,
  mapDispatchToProps,
)(SidebarMenu);
